import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { trackCTAClick, trackSignUpStart, trackPreviewStart } from '../utils/analytics';

const joinThoughts = [
  { text: "I don't know if I'm making the right choices", className: 'top-[10%] left-[2%]' },
  { text: 'Something in my life does not feel right', className: 'top-[14%] right-[2%]' },
  { text: 'I keep second-guessing everything', className: 'top-[34%] left-[1%]' },
  { text: 'I do not understand what I really want', className: 'top-[36%] right-[1%]' },
 ];

// Top bar
const TopBar = () => (
  <nav className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-6 sm:px-10 py-5">
    <Link to="/" className="text-indigo-200 font-serif text-xl tracking-wider hover:text-white transition-colors">
      Andlegur
    </Link>
    <Link to="/login" className="text-indigo-400 hover:text-indigo-200 text-sm transition-colors">
      Sign in
    </Link>
  </nav>
);

export default function JoinPage() {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Join Andlegur Free | Your Private Clarity Space</title>
        <meta name="description" content="Join Andlegur free and start your private reflection space on web. Get clarity for life decisions and track your growth." />
        <meta name="keywords" content="create Andlegur account, join Andlegur, personal clarity app, reflection account, private growth journal" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://andlegur.com/join" />
        <meta property="og:title" content="Join Andlegur Free" />
        <meta property="og:description" content="Your private web space to reflect, get clarity, and grow intentionally." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://andlegur.com/join" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Join Andlegur Free" />
        <meta name="twitter:description" content="Create your account and start reflecting with clarity on web." />
      </Helmet>

      <div className="relative min-h-screen overflow-hidden bg-[#05091B] font-serif text-indigo-100">
        <TopBar />

        <div className="absolute inset-0 z-0 pointer-events-none">
          <img
            src="/web_images/Andlegur%20web%2010.jpg"
            alt=""
            className="w-full h-full object-cover opacity-20 mix-blend-screen"
            loading="eager"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-linear-to-br from-[#05091B]/30 via-transparent to-[#05091B]" />
          <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-transparent to-[#05091B]/50" />
        </div>

        <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 sm:px-10 pt-24 pb-10">
          <div className="pointer-events-none absolute inset-0 z-10 hidden lg:block">
            {joinThoughts.map((item, i) => {
              const bubbleTheme =
                i % 2 === 0
                  ? 'border-indigo-300/30 bg-[#0D1735]/78 text-indigo-100'
                  : 'border-violet-300/35 bg-[#2A1A4A]/70 text-violet-100';

              return (
                <div
                  key={item.text}
                  className={`absolute ${item.className} max-w-72 rounded-2xl border px-4 py-3 text-sm text-left leading-relaxed shadow-lg backdrop-blur-md ${bubbleTheme}`}
                >
                  {item.text}
                </div>
              );
            })}
          </div>

          <div className="w-full max-w-3xl bg-[#0E1833]/85 backdrop-blur-md border border-indigo-500/40 rounded-3xl p-6 sm:p-10 lg:p-12 text-center shadow-2xl">
            <p className="text-indigo-300 text-sm sm:text-base tracking-wide mb-5">Andlegur • Deep Personal Insights</p>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-6">
              Try Andlegur Today.
              <br />
              Judge it.
            </h1>

      

            <div className="grid gap-3 text-left max-w-xl mx-auto mb-10 text-indigo-100 text-base sm:text-lg">
              <p className="flex items-start gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-indigo-300" />Keep your reflections private and organized</p>
              <p className="flex items-start gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-indigo-300" />Turn heavy thoughts into clear decisions</p>
              <p className="flex items-start gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-indigo-300" />Track personal growth over time</p>
            </div>

            <button
              onClick={() => { trackCTAClick('create_free_account', '/join'); trackSignUpStart(); navigate('/register'); }}
              className="w-full sm:w-auto min-w-65 bg-indigo-600 hover:bg-indigo-500 text-white text-lg sm:text-xl font-semibold py-4 px-10 rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-indigo-700/40"
              aria-label="Create your free Andlegur account"
            >
              Create My Free Account
            </button>


            <p className="mt-6 text-indigo-300 text-sm sm:text-base">Private • Free • Your reflections stay yours</p>

            <p className="mt-4 text-indigo-400 text-sm">
              Already a member?{' '}
              <Link to="/login" className="text-indigo-200 hover:text-white underline underline-offset-4">Sign in</Link>
            </p>

           
          </div>

  

          <Link to="/download" className="mt-3 text-indigo-300 hover:text-white underline underline-offset-4 text-sm sm:text-base">
            Download the app
          </Link>
        </div>
      </div>
    </>
  );
}
